import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { api } from "@/src/core/api/client";
import { WISHLIST_PAGE_SIZE, wishlistService } from "../services";
import type {
  IWishlist,
  IWishlistCreated,
  IWishlistCreateRequest,
  IWishlistEnvelope,
  IWishlistItems,
  IWishlistPage,
} from "../types";

export const wishlistKeys = {
  all: ["wishlists"] as const,
  list: () => [...wishlistKeys.all, "list"] as const,
  detail: (id: number) => [...wishlistKeys.all, "detail", id] as const,
  items: (id: number) => [...wishlistKeys.all, "items", id] as const,
  cover: (id: number) => [...wishlistKeys.all, "cover", id] as const,
};

const page = <T>({ data, pagination }: IWishlistEnvelope<T>) => ({
  data,
  pagination: pagination ?? null,
});

/**
 * Raw calls. Folders live on the Python service (bearer required); recently viewed
 * is Laravel's. Throw an ApiError on failure (FastAPI's `detail` is the message).
 */
export const wishlistApi = {
  async getWishlists(
    pageNumber = 1,
    perPage = WISHLIST_PAGE_SIZE,
  ): Promise<IWishlistPage<IWishlist[]>> {
    const { data } = await api.python.get<IWishlistEnvelope<IWishlist[]>>(
      "/wishlists",
      { params: { page: pageNumber, per_page: perPage } },
    );
    return { ...page(data), data: data.data ?? [] };
  },

  async getWishlist(id: number): Promise<IWishlist> {
    const { data } = await api.python.get<IWishlistEnvelope<IWishlist>>(
      `/wishlists/${id}`,
    );
    return data.data;
  },

  async createWishlist(
    request: IWishlistCreateRequest,
  ): Promise<IWishlistCreated> {
    const { data } = await api.python.post<IWishlistEnvelope<IWishlistCreated>>(
      "/wishlists",
      request,
    );
    return data.data;
  },

  async renameWishlist(id: number, name: string): Promise<void> {
    await api.python.put(`/wishlists/${id}`, { name });
  },

  /** Makes the folder the user's default (Python `PUT /wishlists/{id}` `is_default`). */
  async setDefaultWishlist(id: number): Promise<void> {
    await api.python.put(`/wishlists/${id}`, { is_default: true });
  },

  /** Makes the folder public and returns its share link. */
  async shareWishlist(id: number): Promise<string | null> {
    const { data } = await api.python.put<
      IWishlistEnvelope<{ shared_link?: string | null } | null>
    >(`/wishlists/${id}`, { visibility: "shared" });
    return data.data?.shared_link ?? null;
  },

  async deleteWishlist(id: number): Promise<void> {
    await api.python.delete(`/wishlists/${id}`);
  },

  async getItems(
    id: number,
    pageNumber = 1,
    perPage = WISHLIST_PAGE_SIZE,
  ): Promise<IWishlistPage<IWishlistItems>> {
    const { data } = await api.python.get<IWishlistEnvelope<IWishlistItems>>(
      `/wishlists/${id}/items`,
      { params: { page: pageNumber, per_page: perPage } },
    );
    return page(data);
  },

  async addListing(id: number, listingId: number): Promise<void> {
    await api.python.post(`/wishlists/${id}/items`, { listing_id: listingId });
  },

  async removeListing(id: number, listingId: number): Promise<void> {
    await api.python.delete(`/wishlists/${id}/listings/${listingId}`);
  },

  /** The signed-in user's last 30 viewed listings, newest first (Laravel). */
};

/** The wishlist tab: folders, default first, 15 per page. */
export const useWishlistsQuery = (enabled = true) =>
  useInfiniteQuery({
    queryKey: wishlistKeys.list(),
    queryFn: ({ pageParam }) => wishlistApi.getWishlists(pageParam),
    initialPageParam: 1,
    getNextPageParam: (last) => wishlistService.nextPage(last.pagination),
    enabled,
  });

/** One folder's header (name, visibility, share link). */
export const useWishlistQuery = (id: number | undefined) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: wishlistKeys.detail(id ?? 0),
    queryFn: () => wishlistApi.getWishlist(id as number),
    enabled: id !== undefined,
    // Opened from the grid, the folder is already known: show its name at once.
    placeholderData: () =>
      queryClient
        .getQueryData<InfiniteData<IWishlistPage<IWishlist[]>>>(
          wishlistKeys.list(),
        )
        ?.pages.flatMap((page) => page.data)
        .find((folder) => folder.id === id),
  });
};

/** One folder's listings, newest first, 15 per page. */
export const useWishlistItemsQuery = (id: number | undefined) =>
  useInfiniteQuery({
    queryKey: wishlistKeys.items(id ?? 0),
    queryFn: ({ pageParam }) => wishlistApi.getItems(id as number, pageParam),
    initialPageParam: 1,
    getNextPageParam: (last) => wishlistService.nextPage(last.pagination),
    enabled: id !== undefined,
  });

/** A folder's cover photos: its newest three stays' images (the grid's photo stack). */
export const useWishlistCoverQuery = (id: number, enabled: boolean) =>
  useQuery({
    queryKey: wishlistKeys.cover(id),
    queryFn: () => wishlistApi.getItems(id, 1, 3),
    select: (page) =>
      page.data.listing
        .map((listing) => listing.image_url)
        .filter((url): url is string => !!url),
    enabled,
    staleTime: 5 * 60 * 1000,
  });

/** Renames a folder; the tab and the folder refetch. */
export const useRenameWishlistMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) =>
      wishlistApi.renameWishlist(id, name),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: wishlistKeys.all }),
  });
};

/** Makes a folder the default; the folders reload so the "Default" badge moves. */
export const useSetDefaultWishlistMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => wishlistApi.setDefaultWishlist(id),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: wishlistKeys.all }),
  });
};

/** Makes a folder public; resolves with its share link. */
export const useShareWishlistMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => wishlistApi.shareWishlist(id),
    onSuccess: (_link, id) =>
      queryClient.invalidateQueries({ queryKey: wishlistKeys.detail(id) }),
  });
};
